"use client";

import { Select, SelectProps } from "@/components/Select/Select";
import { getDataLinkAttributes } from "@/helpers";
import { CallbackReturnType } from "@/types";
import { Dropdown } from "@/types/General/Dropdown";
import { BuilderContextInterface } from "@builder.io/sdk-react";
import { FC, PropsWithChildren, useEffect, useMemo, useRef } from "react";

type SelectBlockProps = {
  variant: "form" | "round" | "rectangular" | "large rectangular";
  dlt?: string;
  dld?: string;
  placeholder: string;
  isMultipleSelection?: boolean;
  multipleSelectionDisplayLabel?: string;
  onChange?: (
    value: Dropdown | Dropdown[],
    elem: HTMLDivElement | null,
  ) => CallbackReturnType;
  onInitialize?: (elem: HTMLDivElement | null) => CallbackReturnType;
  onBlur?: (elem: HTMLDivElement | null) => CallbackReturnType;
} & SelectProps & {
    builderContext: BuilderContextInterface;
  };

export const SelectBlock: FC<PropsWithChildren<SelectBlockProps>> = ({
  children: _,
  builderContext,
  options,
  dld,
  dlt,
  placeholder,
  onInitialize,
  onBlur: onBlurProp,
  ...props
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const placeholderValue = useMemo(
    () => ({ label: placeholder, value: "", disabled: true }),
    [placeholder],
  );

  useEffect(() => {
    if (onInitialize) {
      onInitialize(ref.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = async (value: Dropdown | Dropdown[]) => {
    if (props.onChange) {
      await props.onChange(value, ref.current);
    }
  };

  const onBlur = async () => {
    if (onBlurProp) {
      await onBlurProp(ref.current);
    }
  };

  const selectValue =
    props.value ?? (props.isMultipleSelection ? [] : placeholderValue);

  return (
    <Select
      {...props}
      ref={ref}
      value={selectValue}
      options={
        placeholder ? [placeholderValue, ...(options ?? [])] : (options ?? [])
      }
      onChange={onChange}
      onBlur={onBlur}
      multiple={props.isMultipleSelection}
      {...getDataLinkAttributes({ dld, dlt })}
    />
  );
};
